import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Flex, Popconfirm, Table, Typography } from 'antd'
import { type ColumnsType } from 'antd/lib/table'

import { path, useColumnSearch } from 'src/libs'
import { Guard } from 'src/features/session/guard'

import {
  useWorldCupStatus,
  useWinnersList,
  useWinnersListGate,
} from 'src/pages/worldCup2026/winners/hooks'
import {
  changeStatusSubmit,
  onChangeWinnerList,
} from 'src/pages/worldCup2026/winners/model'
import { Winner } from 'src/pages/worldCup2026/winners/types'
import { prizeTypesOptions, PRIZE_TYPES } from 'src/pages/worldCup2026/const'
import { AddWinnersModal } from 'src/pages/worldCup2026/winners/widgets'

export const WinnerList = () => {
  const [isAddWinnersModalOpen, setIsAddWinnersModalOpen] = useState(false)

  const { winners, totalCountOfWinners, loading } = useWinnersList()
  const worldCupStatus = useWorldCupStatus()
  useWinnersListGate()

  const { Text } = Typography

  const clientIdSearch = useColumnSearch<Winner>({
    placeholder: 'clientID',
    customRender: (highlightedValue, record) => (
      <Link
        to={path.clients.details(String(record.client.clientId))}
        style={{ textDecoration: 'none' }}
      >
        {highlightedValue}
      </Link>
    ),
  })
  const clientFullNameSearch = useColumnSearch<Winner>({
    placeholder: 'client Fullname',
  })
  const clientEmailSearch = useColumnSearch<Winner>({
    placeholder: 'client Email',
  })
  const ticketIdSearch = useColumnSearch<Winner>({
    placeholder: 'ticket ID',
  })

  const columns: ColumnsType<Winner> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...clientIdSearch,
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...clientFullNameSearch,
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...clientEmailSearch,
    },
    {
      title: 'Ticket',
      dataIndex: 'ticketId',
      ...ticketIdSearch,
    },
    {
      title: 'Prize',
      dataIndex: 'prizeType',
      render: (type: string) =>
        PRIZE_TYPES[type as keyof typeof PRIZE_TYPES] ?? type,
      filters: prizeTypesOptions.map(({ label, value }) => ({
        text: label,
        value,
      })),
    },
  ]
  const currentStatus = worldCupStatus[0]?.status
  return (
    <Flex vertical gap="middle">
      {currentStatus && (
        <Text style={{ fontSize: 16 }}>{`Status: ${currentStatus}`}</Text>
      )}
      {currentStatus === 'ended' && (
        <Guard permission="world_cup_2026_winners">
          <Flex justify="flex-start" gap="middle">
            <Button
              color="geekblue"
              type="primary"
              size="large"
              onClick={() => setIsAddWinnersModalOpen(true)}
            >
              Add winners
            </Button>
            <Popconfirm
              title="Change status"
              description="Are you sure you want to change status"
              okText="Yes"
              cancelText="No"
              onConfirm={changeStatusSubmit}
            >
              <Button color="geekblue" type="primary" size="large">
                Move to Results
              </Button>
            </Popconfirm>
          </Flex>
        </Guard>
      )}
      <Table
        dataSource={winners}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey={(record) => record.ticketId}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfWinners,
        }}
        onChange={(pagination, filters, sorter: any) =>
          onChangeWinnerList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }
      />
      <AddWinnersModal
        isVisible={isAddWinnersModalOpen}
        onClose={() => setIsAddWinnersModalOpen(false)}
      />
    </Flex>
  )
}
